import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { Field } from './Field';

interface Props {
  open: boolean;
  title: string;
  /** What will happen, in the user's terms. */
  message: string;
  confirmLabel?: string;
  /** When set, the user must type this exact value to enable the button. */
  requireText?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Replaces the five native `confirm()` calls, which were unstyleable,
 * suppressible by the browser, and read identically whether you were deleting
 * one webhook or an entire user account.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  requireText,
  danger = true,
  loading = false,
  onConfirm,
  onClose,
}: Props) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const blocked = requireText !== undefined && typed.trim() !== requireText;

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">{message}</p>

        {requireText !== undefined && (
          <Field label={`Type "${requireText}" to confirm`}>
            {p => (
              <Input
                {...p}
                type="text"
                autoComplete="off"
                value={typed}
                onChange={e => setTyped(e.target.value)}
              />
            )}
          </Field>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            variant={danger ? 'danger' : 'primary'}
            disabled={blocked}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
